import React, { useContext, useEffect, useMemo, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { toast } from "react-toastify";
import {
  Button,
  DialogModal,
  Divider,
  Drawer,
  PanelHeader,
  ReactSelectController,
  Tabs,
  Text,
} from "@pushpress/shared-components";
import { sortByFirstName, sortByName } from "../../../utils/sortUtils";
import MemberSelect from "../../../components/controllers/Deprecated/MemberSelect";
import { useAnalytics } from "../../../components/providers/AnalyticsProvider";
import { useToggle } from "../../../hooks/state";
import { useFeatureFlag } from "../../../context/features";
import { RECURRING_APPT_FEATURE_FLAG } from "../../../context/features/features.utils";
import { AppContext } from "../../../context/app";
import {
  defaultToastOptions,
  extractError,
  joinStrings,
} from "../../../utils/misc";
import {
  AppointmentForm,
  BookAppointmentDrawerProps,
} from "./BookAppointmentDrawer.types";
import {
  convertToBookAppointmentInput,
  convertToCreateTemplateInput,
  getFormDefaultValues,
  showSaveResult,
  validateDays,
} from "./BookAppointmentDrawer.utils";
import SessionsCredit from "./SessionsCredit";
import {
  AppointmentTypeOptionFragment,
  useBookAppointmentMutation,
  useCreateAppointmentTemplateMutation,
  useGetAppointmentTypesForBookingQuery,
  useGetUserCreditsQuery,
} from "./BookAppointmentDrawer-generated.hooks";
import Series from "./Series";
import Individual from "./Individual";

function BookAppointmentDrawer(props: BookAppointmentDrawerProps) {
  const { visible } = props;
  return (
    <Drawer
      visible={visible}
      anchor="right"
      width="400px"
      disableClickAway
      disableEscapeKeyDown
    >
      <BookAppointmentDrawerContent {...props} />
    </Drawer>
  );
}

function BookAppointmentDrawerContent(props: BookAppointmentDrawerProps) {
  const { onClose, defaultValues = {}, onSuccess } = props;

  // custom hooks

  const { client } = useContext(AppContext);
  const { trackEvent } = useAnalytics();
  const { isFeatureEnabled } = useFeatureFlag();
  const recurringBookingFlag = isFeatureEnabled(RECURRING_APPT_FEATURE_FLAG);

  // states

  const [saving, savingActions] = useToggle(false);
  const [confirmCloseVisible, confirmCloseActions] = useToggle(false);
  const [scheduleTab, setScheduleTab] = useState(0);
  const [formDefault] = useState(() => getFormDefaultValues(defaultValues));

  // form

  const {
    control,
    handleSubmit,
    setValue,
    formState: { isDirty, isSubmitted },
  } = useForm<AppointmentForm>({
    mode: "onTouched",
    defaultValues: formDefault,
  });

  const [appointmentType, staff, member, days] = useWatch({
    control,
    name: ["appointmentType", "staff", "member", "days"],
  });

  const daysErrorMessage =
    isSubmitted && scheduleTab === 1 ? validateDays(days) : null;
  const daysHasError = validateDays(days);

  // api

  const appointmentTypesQuery = useGetAppointmentTypesForBookingQuery();

  const appointmentCreditsQuery = useGetUserCreditsQuery({
    variables: {
      input: {
        userUuid: member?.uuid,
        appointmentTypeUuid: appointmentType?.uuid,
      },
    },
    skip: !member?.uuid || !appointmentType?.uuid,
    fetchPolicy: "no-cache",
  });

  const [bookAppointmentMutation] = useBookAppointmentMutation();

  const [createTemplateMutation] = useCreateAppointmentTemplateMutation();

  // callbacks

  const handleClose = () => {
    if (isDirty) {
      confirmCloseActions.on();
    } else {
      onClose();
    }
  };

  const handleAppointmentTypeChange = (type: AppointmentTypeOptionFragment) => {
    const typeHasSelectedStaff = type.staff.some(
      (item) => item.userUuid === staff?.userUuid,
    );
    if (!typeHasSelectedStaff) {
      setValue("staff", undefined);
    }
    if (type.staff.length === 1) {
      setValue("staff", type.staff[0]);
    }
    setValue("when", formDefault?.when);
  };

  const handleMemberChange = () => {
    setValue("when", formDefault?.when);
  };

  const saveIndividuals = async (values: AppointmentForm) => {
    savingActions.on();
    const input = convertToBookAppointmentInput(values);
    try {
      const result = await bookAppointmentMutation({
        variables: {
          input,
        },
      });
      const fulfilledCount =
        result.data.staffBookAppointment.successfulBookings.length;
      const rejectedCount =
        result.data.staffBookAppointment.failedBookings.length;
      showSaveResult(fulfilledCount, rejectedCount);
      trackEvent("cp.appointments.booked", {
        number_appointments: fulfilledCount,
        number_appointments_failed:
          rejectedCount > 0 ? rejectedCount : undefined,
      });
      if (rejectedCount === 0) {
        onSuccess();
      }
    } catch (error) {
      toast.error(extractError(error), defaultToastOptions);
    } finally {
      savingActions.off();
    }
  };

  const saveSeries = async (values: AppointmentForm) => {
    if (daysHasError) {
      return;
    }
    savingActions.on();
    const input = convertToCreateTemplateInput(values, client.timezone);
    try {
      await createTemplateMutation({
        variables: {
          input,
        },
      });
      toast.success("Appointment series created", defaultToastOptions);
      onSuccess();
    } catch (error) {
      toast.error(extractError(error), defaultToastOptions);
    } finally {
      savingActions.off();
    }
  };

  const save = async (values: AppointmentForm) => {
    if (scheduleTab === 0) {
      saveIndividuals(values);
    } else {
      saveSeries(values);
    }
  };

  const handleSave = () => {
    handleSubmit(save)();
  };

  // memos

  const appointmentTypeWithStaff = useMemo<
    AppointmentTypeOptionFragment[]
  >(() => {
    const data = appointmentTypesQuery.data?.getAppointmentTypes || [];
    const result = data.filter((item) => item.staff.length > 0);
    result.sort(sortByName);
    return result;
  }, [appointmentTypesQuery.data]);

  const filteredStaff = useMemo<AppointmentTypeOptionFragment["staff"]>(() => {
    if (!appointmentType) {
      return [];
    }
    const data = appointmentTypesQuery.data?.getAppointmentTypes || [];
    const selectedType = data.find(
      (item) => item.uuid === appointmentType.uuid,
    );
    return [...selectedType.staff].sort(sortByFirstName);
  }, [appointmentTypesQuery.data, appointmentType]);

  // select the appointment type and staff default
  useEffect(() => {
    const staffUuid = defaultValues?.staffUuid;
    if (staffUuid) {
      const typeWithTheSelectedStaff = appointmentTypeWithStaff.find((type) =>
        type.staff.some((s) => s.userUuid === staffUuid),
      );
      if (typeWithTheSelectedStaff) {
        const staff = typeWithTheSelectedStaff.staff.find(
          (s) => s.userUuid === staffUuid,
        );
        setValue("appointmentType", typeWithTheSelectedStaff);
        setValue("staff", staff);
      } else {
        setValue("appointmentType", appointmentTypeWithStaff[0]);
      }
    } else {
      if (appointmentTypeWithStaff.length > 0) {
        setValue("appointmentType", appointmentTypeWithStaff[0]);
      }
    }
  }, [appointmentTypeWithStaff, defaultValues, setValue]);

  if (appointmentTypesQuery.loading) {
    return null;
  }

  const credits =
    appointmentCreditsQuery.data?.getUserAppointmentCreditsCountByType?.count;
  const creditsLoading =
    appointmentCreditsQuery.loading && !appointmentCreditsQuery.data;
  const saveDisabled = recurringBookingFlag ? false : !credits;
  const scheduleVisible = recurringBookingFlag ? true : !!credits;
  const individualVisible = scheduleTab === 0;
  const seriesVisible = scheduleTab === 1;

  return (
    <>
      <PanelHeader title="Book appointment" stickyTop={0}>
        <PanelHeader.ActionMenu>
          <Button
            text="Close"
            buttonType="secondary"
            size="medium"
            onClick={handleClose}
          />
          <Button
            text="Save"
            buttonType="primary"
            size="medium"
            type="button"
            loading={saving}
            disabled={saveDisabled}
            onClick={handleSave}
          />
        </PanelHeader.ActionMenu>
      </PanelHeader>
      <div style={{ padding: 24 }}>
        <form onSubmit={handleSubmit(save)}>
          <ReactSelectController
            label="Type"
            control={control}
            name="appointmentType"
            required
            options={appointmentTypeWithStaff}
            enableOptionColor
            enableSingleValueColor
            getOptionValue={(option) => option.uuid}
            getOptionLabel={(option) => option.name}
            getOptionColor={(option) => option.color}
            onChange={handleAppointmentTypeChange}
          />
          <ReactSelectController
            label="Staff"
            control={control}
            name="staff"
            required
            options={filteredStaff}
            getOptionValue={(option) => option.userUuid}
            getOptionLabel={(option) =>
              joinStrings(option.firstName, option.lastName)
            }
          />
          <MemberSelect
            label="Client"
            placeholder="Search for a member or lead"
            control={control}
            name="member"
            required
            hideDropdownIndicator
            isClearable
            onChange={handleMemberChange}
          />
          <SessionsCredit
            appointmentTypeUuid={appointmentType?.uuid}
            userUuid={member?.uuid}
            credits={credits}
            loading={creditsLoading}
            onSuccess={appointmentCreditsQuery.refetch}
          />
          {scheduleVisible && (
            <>
              <Divider marginRight={-24} vPadding={28} />
              <Text
                component="h2"
                variant="heading-5"
                color="wolf-800"
                className="mb-1"
              >
                Schedule
              </Text>
              {recurringBookingFlag && (
                <>
                  <Text variant="body-md" color="wolf-600" className="mb-4">
                    Book individual appointments, or a recurring series
                  </Text>
                  <Tabs
                    tabHeaders={["Individual", "Recurring"]}
                    selectedIndex={scheduleTab}
                    variant="buttons"
                    className="mb-4"
                    changeTab={setScheduleTab}
                  />
                </>
              )}
              {individualVisible && (
                <Individual credits={credits} control={control} />
              )}
              {seriesVisible && (
                <Series
                  control={control}
                  setValue={setValue}
                  daysErrorMessage={daysErrorMessage}
                />
              )}
            </>
          )}
        </form>
      </div>
      <DialogModal
        title="Discard unsaved changes?"
        visible={confirmCloseVisible}
        primaryBtnTitle="Discard"
        secondaryBtnTitle="Go back"
        primaryHandler={onClose}
        secondaryHandler={confirmCloseActions.off}
        closeModal={confirmCloseActions.off}
      />
    </>
  );
}

export default BookAppointmentDrawer;
